'use client';

import { useState, useMemo } from 'react';
import Image from 'next/image';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';

type LightboxImage =
  | string
  | {
      src: string;
      alt?: string;
      title?: string;
      desc?: string; // shown as caption description
    };

export default function EventLightbox({
  images,
  title,
}: {
  images: LightboxImage[];
  title: string;
}) {
  const [index, setIndex] = useState(-1);

  // normalize all inputs to objects
  const items = useMemo(
    () =>
      (images || []).map((img) =>
        typeof img === 'string' ? { src: img } : img
      ),
    [images]
  );

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((img, i) => (
          <button
            type="button"
            key={(img.src || 'img') + i}
            className="relative aspect-video rounded-xl overflow-hidden group focus:outline-none"
            onClick={() => setIndex(i)}
          >
            <Image
              src={img.src}
              alt={img.alt || `${title} - ${i + 1}`}
              fill
              sizes="(min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw"
              className="object-cover transition-transform duration-300 group-hover:scale-105"
              priority={i < 3} // eager-load first row
            />
          </button>
        ))}
      </div>

      <Lightbox
        open={index >= 0}
        close={() => setIndex(-1)}
        index={index}
        slides={items.map((img) => ({
          src: img.src,
          alt: img.alt,
          title: img.title,        // shown by Captions plugin as heading
          description: img.desc,   // shown by Captions plugin as body
        }))}
        plugins={[Zoom, Captions]}
        // Optional: nicer zoom defaults
        zoom={{ maxZoomPixelRatio: 2, zoomInMultiplier: 1.2 }}
        // Optional: basic captions styling via classNames (if you want)
        // className="your-custom-lightbox-styles"
      />
    </>
  );
}
